import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  LIBELLES_STATUT,
  mentionMiseAJour,
  moisSansMiseAJour,
  periodeAnnonce,
  type AnnonceVivier,
  type CandidatureDetail,
  type CandidatureResume,
  type ChoixVivier,
  type TexteVersion,
} from '@rh/shared';
import { appel } from '../api';
import { classeNom, nomComplet } from '../format';
import { Chargement, Erreur, Vide } from './Etat';
import { Note, Pastille } from './Pastille';

interface Props {
  annonceId: string;
  rh: boolean;
  onRattachee: (fiche: CandidatureDetail) => void;
}

/**
 * Panneau « Inclure les anciennes candidatures » (docs/UI.md § Écran 1) :
 * choix des annonces précédentes, mémorisé par annonce et par utilisateur,
 * puis anciens candidats teintés, à rattacher à l'annonce courante.
 */
export function AnciennesCandidatures({ annonceId, rh, onRattachee }: Props) {
  const [choix, setChoix] = useState<ChoixVivier | null>(null);
  const [anciennes, setAnciennes] = useState<CandidatureResume[] | null>(null);
  const [texte, setTexte] = useState<{ id: string; contenu: string } | null>(null);
  const [enCours, setEnCours] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    setChoix(null);
    appel<ChoixVivier>(`/annonces/${annonceId}/vivier`)
      .then(setChoix)
      .catch((e: Error) => setErreur(e.message));
  }, [annonceId]);

  const selection = choix?.selection.join(',') ?? '';
  const qualite = choix?.qualite ?? true;
  useEffect(() => {
    if (!choix) return;
    if (!selection) {
      setAnciennes([]);
      return;
    }
    setAnciennes(null);
    appel<CandidatureResume[]>(
      `/annonces/${annonceId}/vivier/candidatures?annonces=${selection}&qualite=${qualite}`,
    )
      .then(setAnciennes)
      .catch((e: Error) => setErreur(e.message));
    // `choix` sert de garde de chargement : seules la sélection et la qualité relancent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annonceId, selection, qualite, choix === null]);

  if (!choix) return erreur ? <Erreur message={erreur} /> : <Chargement quoi="des annonces précédentes" />;

  async function memoriser(annonces: string[], filtreQualite: boolean): Promise<void> {
    setErreur(null);
    setChoix((avant) => avant && { ...avant, selection: annonces, qualite: filtreQualite });
    try {
      await appel<ChoixVivier>(`/annonces/${annonceId}/vivier`, {
        method: 'PUT',
        body: JSON.stringify({ annonces, qualite: filtreQualite }),
      });
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Sélection non enregistrée');
    }
  }

  function basculer(id: string): void {
    const courante = choix!.selection;
    void memoriser(courante.includes(id) ? courante.filter((a) => a !== id) : [...courante, id], choix!.qualite);
  }

  async function lireTexte(annonce: AnnonceVivier): Promise<void> {
    if (texte?.id === annonce.id) {
      setTexte(null);
      return;
    }
    try {
      const version = await appel<TexteVersion>(`/annonces/${annonce.id}/versions/${annonce.version}`);
      const contenu = version.textes.fr ?? version.textes.ar ?? Object.values(version.textes)[0] ?? '';
      setTexte({ id: annonce.id, contenu });
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Texte indisponible');
    }
  }

  async function rattacher(candidature: CandidatureResume): Promise<void> {
    setErreur(null);
    setEnCours(candidature.id);
    try {
      const fiche = await appel<CandidatureDetail>(`/candidatures/${candidature.id}/rattacher`, {
        method: 'POST',
        body: JSON.stringify({ annonceCibleId: annonceId }),
      });
      setAnciennes((liste) => liste?.filter((c) => c.personneId !== candidature.personneId) ?? null);
      onRattachee(fiche);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Rattachement impossible');
    } finally {
      setEnCours(null);
    }
  }

  const memeFamille = choix.annonces.filter((a) => a.memeFamille);
  const autres = choix.annonces.filter((a) => !a.memeFamille);
  const autresChoisies = autres.filter((a) => choix.selection.includes(a.id)).length;

  const ligneAnnonce = (annonce: AnnonceVivier) => (
    <li key={annonce.id} className="annonce-vivier">
      <label className="case">
        <input
          type="checkbox"
          checked={choix.selection.includes(annonce.id)}
          onChange={() => basculer(annonce.id)}
        />
        <span>
          <code>{annonce.reference}</code> {annonce.intitule}
        </span>
      </label>
      <p className="mention">
        {periodeAnnonce(annonce)} · {annonce.candidats} candidat{annonce.candidats > 1 ? 's' : ''} ·{' '}
        {annonce.evalues} évalué{annonce.evalues > 1 ? 's' : ''}
        {' · '}
        {annonce.version !== null ? (
          <button type="button" className="lien" onClick={() => void lireTexte(annonce)}>
            {texte?.id === annonce.id ? 'Masquer le texte' : 'Texte de l’époque'}
          </button>
        ) : (
          'texte non saisi'
        )}
      </p>
      {texte?.id === annonce.id && (
        <pre className="texte-epoque" dir="auto">
          {texte.contenu}
        </pre>
      )}
    </li>
  );

  return (
    <div className="vivier">
      <fieldset className="vivier-annonces">
        <legend>Annonces précédentes</legend>
        {memeFamille.length > 0 ? (
          <ul>{memeFamille.map(ligneAnnonce)}</ul>
        ) : (
          <p className="mention">Aucune annonce précédente pour ce métier.</p>
        )}
        {autres.length > 0 && (
          <details>
            <summary>
              Autres métiers ({autres.length}){autresChoisies > 0 && ` — ${autresChoisies} cochée(s)`}
            </summary>
            <ul>{autres.map(ligneAnnonce)}</ul>
          </details>
        )}
        <label className="case">
          <input
            type="checkbox"
            checked={choix.qualite}
            onChange={() => void memoriser(choix.selection, !choix.qualite)}
          />
          <span>Seulement note ou pastille ≥ {choix.noteMinimum}</span>
        </label>
      </fieldset>

      {erreur && <Erreur message={erreur} />}
      {choix.selection.length === 0 && <Vide enfants="Cocher une annonce pour retrouver ses candidats." />}
      {choix.selection.length > 0 && !anciennes && <Chargement quoi="des anciens candidats" />}
      {anciennes && choix.selection.length > 0 && anciennes.length === 0 && (
        <Vide enfants="Aucun ancien candidat ne correspond." />
      )}

      <ul className="candidats">
        {(anciennes ?? []).map((candidature) => (
          <li key={candidature.id} className="carte-candidat ancienne">
            <Link to={`/candidatures/${candidature.id}`} className="carte-lien">
              <Pastille valeur={candidature.potentiel} libelle etiquette={candidature.etiquette} />
              <div className="corps">
                <div className="ligne1">
                  <strong className={classeNom(candidature.statut)}>{nomComplet(candidature)}</strong>
                  <span className="badge-origine">{candidature.annonceReference}</span>
                </div>
                <p className="synthese">
                  {candidature.annonceIntitule} · {LIBELLES_STATUT[candidature.statut]}
                </p>
                <div className="meta">
                  {candidature.ville && <span>{candidature.ville}</span>}
                  <span>{candidature.source}</span>
                  <span>{mentionMiseAJour(moisSansMiseAJour(candidature))}</span>
                </div>
              </div>
              <Note valeur={candidature.noteEntretien} date={candidature.noteLe} />
            </Link>
            {rh && (
              <button
                type="button"
                className="appeler-mini rattacher"
                disabled={enCours !== null}
                onClick={() => void rattacher(candidature)}
              >
                {enCours === candidature.id ? 'Rattachement…' : 'Rattacher à cette annonce'}
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
